import { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";
import { Link } from "react-router-dom";
import { Pause, Play, Plus, Send, Square, UserPlus } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import {
    OUTBOUND_COHORTS_URL, OUTBOUND_PROSPECTS_URL, OUTBOUND_SEQUENCES_URL,
    outboundSequenceEnrollUrl, outboundSequenceUrl,
} from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Btn, inputCls, OutboundHeader, Pill } from "./ui";

/**
 * Outbound -> Sequences. A sequence is up to three emails run on a cohort (a
 * saved filter over prospects and the facts read from their websites).
 */

const SEQ_TONE = { draft: "gray", active: "green", paused: "amber", completed: "gray" };
const EMPTY = { cities: [], categories: [], min_score: 0, require: [], exclude: [] };

function SignalPicker({ signals, value, onChange, tone }) {
    return (
        <div className="flex flex-wrap gap-1">
            {signals.map(s => {
                const on = value.includes(s.code);
                return (
                    <button key={s.code} type="button" title={s.help}
                        onClick={() => onChange(on ? value.filter(x => x !== s.code) : [...value, s.code])}
                        className={cn("rounded-full border px-2 py-0.5 text-[11px]",
                            on ? (tone === "red" ? "border-red-400 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300" : "border-primary bg-primary/10 text-primary")
                                : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300")}>
                        {s.label}
                    </button>
                );
            })}
        </div>
    );
}

function NewSequence({ open, onClose, onCreated }) {
    const { fetch, create } = useApiMethods();
    const [form, setForm] = useState({ name: "", angle: "", steps: 3 });
    const [filters, setFilters] = useState(EMPTY);
    const [meta, setMeta] = useState({ cities: [], signals: [] });
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        fetch(OUTBOUND_PROSPECTS_URL, { page: 1 }).then(res => {
            if (res?.status === "success") setMeta({ cities: res.cities, signals: res.signals });
        });
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return undefined;
        const t = setTimeout(() => {
            create(OUTBOUND_COHORTS_URL, { preview: true, filters }).then(res => {
                if (res?.status === "success") setPreview(res);
            });
        }, 350);
        return () => clearTimeout(t);
    }, [filters, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        setBusy(true);
        try {
            const cohort = await create(OUTBOUND_COHORTS_URL, { name: form.name, filters });
            if (cohort?.status !== "success") { ToastMsg("error", cohort?.message || "Could not save cohort"); return; }
            const res = await create(OUTBOUND_SEQUENCES_URL, { ...form, cohort_id: cohort.data.id });
            if (res?.status === "success") { ToastMsg("success", "Sequence created"); onCreated(); onClose(); setForm({ name: "", angle: "", steps: 3 }); setFilters(EMPTY); }
            else ToastMsg("error", res?.message || "Could not create");
        } finally { setBusy(false); }
    };
    const f = (k, v) => setFilters(x => ({ ...x, [k]: v }));

    return (
        <Modal open={open} onCancel={onClose} width={760} title="New sequence" okText="Create"
            onOk={save} okButtonProps={{ disabled: !form.name.trim() || busy }}>
            <div className="space-y-4 text-xs">
                <div className="grid gap-3 md:grid-cols-3">
                    <label className="md:col-span-2 space-y-1"><span className="font-medium">Name</span>
                        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Coimbatore hospitals without online booking" className={inputCls} />
                    </label>
                    <label className="space-y-1"><span className="font-medium">Emails</span>
                        <select value={form.steps} onChange={e => setForm({ ...form, steps: Number(e.target.value) })} className={inputCls}>
                            <option value={1}>1 email</option><option value={2}>2 emails</option><option value={3}>3 emails</option>
                        </select>
                    </label>
                </div>
                <label className="block space-y-1"><span className="font-medium">Angle for the writer</span>
                    <textarea rows={3} value={form.angle} onChange={e => setForm({ ...form, angle: e.target.value })} className={inputCls}
                        placeholder="e.g. Lead with online appointment booking and WhatsApp reminders; offer a 15-minute demo. Mention we work with hospitals in Tamil Nadu." />
                </label>

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
                    <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-900 dark:text-white">Who gets it</p>
                        <Pill tone="brand">{preview ? `${preview.size} prospect(s) with a usable email` : "…"}</Pill>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                        <label className="space-y-1"><span className="font-medium">Cities</span>
                            <select multiple value={filters.cities} onChange={e => f("cities", [...e.target.selectedOptions].map(o => o.value))} className={cn(inputCls, "h-24")}>
                                {meta.cities.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <div className="space-y-2">
                            <label className="space-y-1 block"><span className="font-medium">Facility types</span>
                                <div className="flex flex-wrap gap-2">
                                    {["hospital", "clinic", "doctors", "dentist", "laboratory"].map(c => (
                                        <label key={c} className="flex items-center gap-1">
                                            <input type="checkbox" checked={filters.categories.includes(c)}
                                                onChange={() => f("categories", filters.categories.includes(c) ? filters.categories.filter(x => x !== c) : [...filters.categories, c])} /> {c}
                                        </label>
                                    ))}
                                </div>
                            </label>
                            <label className="space-y-1 block"><span className="font-medium">Minimum score</span>
                                <input type="number" min={0} max={100} value={filters.min_score} onChange={e => f("min_score", Number(e.target.value) || 0)} className={cn(inputCls, "w-24")} />
                            </label>
                        </div>
                    </div>
                    <div className="space-y-1"><span className="font-medium">Must have</span>
                        <SignalPicker signals={meta.signals} value={filters.require} onChange={v => f("require", v)} />
                    </div>
                    <div className="space-y-1"><span className="font-medium">Must not have</span>
                        <SignalPicker signals={meta.signals} value={filters.exclude} onChange={v => f("exclude", v)} tone="red" />
                    </div>
                    {preview?.sample?.length > 0 && (
                        <p className="text-gray-500">e.g. {preview.sample.slice(0, 5).map(p => p.name).join(", ")}{preview.size > 5 ? "…" : ""}</p>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default function Sequences() {
    const { fetch, create, update } = useApiMethods();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);

    const load = useCallback(() => {
        fetch(OUTBOUND_SEQUENCES_URL).then(res => {
            if (res?.status === "success") setRows(res.data);
            else ToastMsg("error", res?.message || "Could not load sequences");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const setStatus = async (s, status) => {
        const res = await update(outboundSequenceUrl(s.id), { status });
        if (res?.status === "success") load();
        else ToastMsg("error", res?.message || "Could not update");
    };
    const enrolCohort = async (s) => {
        const res = await create(outboundSequenceEnrollUrl(s.id), { cohort: true, limit: 100 });
        if (res?.status === "success") {
            const why = Object.entries(res.skipped || {}).map(([k, v]) => `${v} ${k}`).join(", ");
            ToastMsg("success", res.message + (why ? ` Skipped: ${why}.` : ""));
            load();
        } else ToastMsg("error", res?.message || "Could not add");
    };

    return (
        <ContainerCard>
            <div className="p-4 space-y-4 overflow-auto">
                <OutboundHeader icon={Send} title="Sequences"
                    subtitle="Up to three emails per prospect. Adding prospects queues them for the AI Worker; their drafts then wait in the Outbox for approval.">
                    <Btn variant="primary" onClick={() => setOpen(true)}><Plus className="h-3.5 w-3.5" /> New sequence</Btn>
                </OutboundHeader>

                {!rows ? <p className="text-xs text-gray-400">Loading…</p> : rows.length === 0 ? (
                    <p className="text-xs text-gray-400">No sequences yet.</p>
                ) : (
                    <div className="grid gap-3 lg:grid-cols-2">
                        {rows.map(s => {
                            const c = s.enrollment_counts || {};
                            return (
                                <div key={s.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3 text-xs">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{s.name}</p>
                                            <p className="text-gray-500">{s.steps} email(s){s.cohort_name ? ` · cohort: ${s.cohort_name}` : ""} · created {s.created_date}</p>
                                        </div>
                                        <Pill tone={SEQ_TONE[s.status]}>{s.status}</Pill>
                                    </div>
                                    {s.angle && <p className="text-gray-600 dark:text-gray-300 line-clamp-2">{s.angle}</p>}
                                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                                        {[["Enrolled", s.enrolled], ["To draft", c.pending_draft || 0], ["To approve", s.drafts_waiting],
                                          ["Sent", s.sent], ["Replied", s.replied], ["Positive", s.positive]].map(([k, v]) => (
                                            <div key={k} className="rounded-lg bg-gray-50 dark:bg-gray-800 py-1.5">
                                                <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">{v}</p>
                                                <p className="text-[10px] text-gray-500">{k}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {s.status !== "completed" && s.cohort_id && (
                                            <Btn onClick={() => enrolCohort(s)}><UserPlus className="h-3.5 w-3.5" /> Add cohort (up to 100)</Btn>
                                        )}
                                        {s.status === "active" && <Btn onClick={() => setStatus(s, "paused")}><Pause className="h-3.5 w-3.5" /> Pause</Btn>}
                                        {s.status === "paused" && <Btn onClick={() => setStatus(s, "active")}><Play className="h-3.5 w-3.5" /> Resume</Btn>}
                                        {s.status !== "completed" && <Btn variant="danger" onClick={() => setStatus(s, "completed")}><Square className="h-3.5 w-3.5" /> End</Btn>}
                                        <Link to={`/outbound/outbox?sequence=${s.id}`} className="h-8 px-3 rounded-md text-xs font-medium inline-flex items-center text-primary">Open outbox</Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <NewSequence open={open} onClose={() => setOpen(false)} onCreated={load} />
        </ContainerCard>
    );
}
